<template>
    <div class="posts-deals-page">
        <BackendHeader />

        <div class="posts-deals-container">
            <div class="page-header">
                <h2>
                    Posts &amp; Deals
                    <span v-if="totalCount != null" class="header-total">— <strong>{{ totalCount.toLocaleString() }}</strong> results</span>
                </h2>
                <div class="controls">
                    <input
                        v-model="searchText"
                        @input="onSearchInput"
                        type="text"
                        placeholder="Search text, ASIN, promo code, retailer..."
                        class="form-input search-input"
                    />
                    <select v-model="stateFilter" @change="loadData" class="form-input">
                        <option value="">All States</option>
                        <option value="NEW">NEW</option>
                        <option value="PROCESSED">PROCESSED</option>
                        <option value="FAILED">FAILED</option>
                        <option value="SKIPPED">SKIPPED</option>
                    </select>
                    <select v-model.number="categoryFilter" @change="onCategoryChange" class="form-input">
                        <option :value="0">All Categories</option>
                        <option v-for="c in topCategories" :key="c.categoryId" :value="c.categoryId">
                            {{ c.name }} ({{ c.dealCount }})
                        </option>
                    </select>
                    <label class="archive-toggle" title="Include deals older than 24 hours">
                        <input type="checkbox" v-model="includeArchived" @change="onColumnFilterNow" />
                        Show archived
                    </label>
                    <button @click="loadData" class="button button-primary" :disabled="loading">
                        {{ loading ? 'Loading...' : 'Refresh' }}
                    </button>
                </div>
            </div>

            <div v-if="error" class="error-message">{{ error }}</div>

            <div class="table-wrap">
                <table class="grid-table">
                    <thead>
                        <tr>
                            <th @click="setSort('PostId')" class="sortable">
                                ID <span class="sort-icon">{{ sortIcon('PostId') }}</span>
                            </th>
                            <th class="col-source">Source</th>
                            <th @click="setSort('PostState')" class="sortable">
                                Post State <span class="sort-icon">{{ sortIcon('PostState') }}</span>
                            </th>
                            <th @click="setSort('Retailer')" class="sortable">
                                Retailer <span class="sort-icon">{{ sortIcon('Retailer') }}</span>
                            </th>
                            <th @click="setSort('Brand')" class="sortable">
                                Brand <span class="sort-icon">{{ sortIcon('Brand') }}</span>
                            </th>
                            <th @click="setSort('Category')" class="sortable">
                                Category <span class="sort-icon">{{ sortIcon('Category') }}</span>
                            </th>
                            <th @click="setSort('FinalPrice')" class="sortable">
                                Price <span class="sort-icon">{{ sortIcon('FinalPrice') }}</span>
                            </th>
                            <th @click="setSort('PctOff')" class="sortable">
                                % Off <span class="sort-icon">{{ sortIcon('PctOff') }}</span>
                            </th>
                            <th>Text</th>
                            <th>Img</th>
                            <th>Price History</th>
                            <th @click="setSort('GaugeTier')" class="sortable">
                                Grade <span class="sort-icon">{{ sortIcon('GaugeTier') }}</span>
                            </th>
                            <th @click="setSort('AudienceScore')" class="sortable">
                                Audience <span class="sort-icon">{{ sortIcon('AudienceScore') }}</span>
                            </th>
                            <th>Type</th>
                            <th @click="setSort('PromoCode')" class="sortable">
                                Promo <span class="sort-icon">{{ sortIcon('PromoCode') }}</span>
                            </th>
                            <th @click="setSort('Stock')" class="sortable">
                                Stock <span class="sort-icon">{{ sortIcon('Stock') }}</span>
                            </th>
                            <th @click="setSort('SalesRank')" class="sortable">
                                Rank <span class="sort-icon">{{ sortIcon('SalesRank') }}</span>
                            </th>
                            <th @click="setSort('BuyBoxSeller')" class="sortable">
                                Seller <span class="sort-icon">{{ sortIcon('BuyBoxSeller') }}</span>
                            </th>
                            <th @click="setSort('CreatedAt')" class="sortable">
                                Created <span class="sort-icon">{{ sortIcon('CreatedAt') }}</span>
                            </th>
                            <th>Link</th>
                            <th>JSON</th>
                        </tr>
                        <tr class="filter-row">
                            <th><input v-model="fPostId" @input="onColumnFilter" placeholder="ID…" class="col-filter" inputmode="numeric" /></th>
                            <th><input v-model="fSource" @input="onColumnFilter" placeholder="Source…" class="col-filter" /></th>
                            <th>
                                <select v-model="stateFilter" @change="onColumnFilterNow" class="col-filter">
                                    <option value="">Any</option>
                                    <option value="NEW">NEW</option>
                                    <option value="PROCESSED">PROCESSED</option>
                                    <option value="FAILED">FAILED</option>
                                    <option value="SKIPPED">SKIPPED</option>
                                </select>
                            </th>
                            <th>
                                <select v-model="fRetailer" @change="onColumnFilterNow" class="col-filter">
                                    <option value="">Any</option>
                                    <option value="amazon">amazon</option>
                                    <option value="walmart">walmart</option>
                                    <option value="target">target</option>
                                    <option value="other">other</option>
                                </select>
                            </th>
                            <th><input v-model="fBrand" @input="onColumnFilter" placeholder="Brand…" class="col-filter" /></th>
                            <th>
                                <select v-model.number="categoryFilter" @change="onColumnFilterNow" class="col-filter">
                                    <option :value="0">Any</option>
                                    <option v-for="c in topCategories" :key="c.categoryId" :value="c.categoryId">{{ c.name }}</option>
                                </select>
                            </th>
                            <th>
                                <div class="range-filter">
                                    <input v-model="fFinalMin" @input="onColumnFilter" placeholder="min" class="col-filter range-input" inputmode="decimal" />
                                    <input v-model="fFinalMax" @input="onColumnFilter" placeholder="max" class="col-filter range-input" inputmode="decimal" />
                                </div>
                            </th>
                            <th>
                                <div class="range-filter">
                                    <input v-model="fPctOffMin" @input="onColumnFilter" placeholder="min" class="col-filter range-input" inputmode="numeric" />
                                    <input v-model="fPctOffMax" @input="onColumnFilter" placeholder="max" class="col-filter range-input" inputmode="numeric" />
                                </div>
                            </th>
                            <th></th>
                            <th></th>
                            <th></th>
                            <th>
                                <select v-model="fGrade" @change="onColumnFilterNow" class="col-filter">
                                    <option value="">Any</option>
                                    <option value="Great">Great</option>
                                    <option value="Good">Good</option>
                                    <option value="Sale">Sale</option>
                                    <option value="Regular">Regular</option>
                                </select>
                            </th>
                            <th>
                                <div class="range-filter">
                                    <input v-model="fAudienceMin" @input="onColumnFilter" placeholder="min" class="col-filter range-input" inputmode="numeric" />
                                    <input v-model="fAudienceMax" @input="onColumnFilter" placeholder="max" class="col-filter range-input" inputmode="numeric" />
                                </div>
                            </th>
                            <th>
                                <select v-model="fType" @change="onColumnFilterNow" class="col-filter">
                                    <option value="">Any</option>
                                    <option :value="1">Sale</option>
                                    <option :value="2">Coupon</option>
                                    <option :value="3">Promo</option>
                                    <option :value="4">Sale+Coupon</option>
                                    <option :value="5">Sale+Promo</option>
                                    <option :value="6">Sale+Coupon+Promo</option>
                                    <option :value="7">Coupon+Promo</option>
                                    <option :value="8">Good Price</option>
                                </select>
                            </th>
                            <th><input v-model="fPromo" @input="onColumnFilter" placeholder="Code…" class="col-filter" /></th>
                            <th>
                                <div class="range-filter">
                                    <input v-model="fStockMin" @input="onColumnFilter" placeholder="min" class="col-filter range-input" inputmode="numeric" />
                                    <input v-model="fStockMax" @input="onColumnFilter" placeholder="max" class="col-filter range-input" inputmode="numeric" />
                                </div>
                            </th>
                            <th>
                                <div class="range-filter">
                                    <input v-model="fRankMin" @input="onColumnFilter" placeholder="min" class="col-filter range-input" inputmode="numeric" />
                                    <input v-model="fRankMax" @input="onColumnFilter" placeholder="max" class="col-filter range-input" inputmode="numeric" />
                                </div>
                            </th>
                            <th>
                                <select v-model="fSeller" @change="onColumnFilterNow" class="col-filter">
                                    <option value="">Any</option>
                                    <option value="amazon">Amazon</option>
                                    <option value="third-party">3rd-party</option>
                                </select>
                            </th>
                            <th></th>
                            <th></th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="loading && rows.length === 0">
                            <td colspan="22" class="loading-cell">Loading...</td>
                        </tr>
                        <tr v-else-if="rows.length === 0">
                            <td colspan="22" class="empty-cell">No results found.</td>
                        </tr>
                        <tr
                            v-for="row in displayRows"
                            :key="row.postId"
                            :class="stateClass(row.postState)"
                        >
                            <td class="mono">{{ row.postId }}</td>
                            <td class="source-cell" :title="row.source">{{ shortSource(row.source) }}</td>
                            <td>
                                <span class="badge" :class="badgeClass(row.postState)">{{ row.postState }}</span>
                            </td>
                            <td>{{ row.retailer || '—' }}</td>
                            <td class="brand-cell">{{ row.brand || '—' }}</td>
                            <td class="cat-cell">{{ row.category || '—' }}</td>
                            <td class="price-cell-combined">
                                <div class="p-final">{{ formatPrice(row.finalPrice) }}</div>
                                <div v-if="row.salePrice != null && row.salePrice !== row.finalPrice" class="p-strike">{{ formatPrice(row.salePrice) }}</div>
                                <div v-if="row.listPrice != null && row.listPrice !== row.salePrice && row.listPrice !== row.finalPrice" class="p-strike p-faint">{{ formatPrice(row.listPrice) }}</div>
                            </td>
                            <td class="pct-off-cell">
                                <span v-if="pctOff(row) !== null" class="pct-off" :class="pctOffClass(pctOff(row)!)">
                                    {{ pctOff(row) }}% off
                                </span>
                                <span v-else class="muted">—</span>
                            </td>
                            <td class="text-cell">
                                <span class="text-link" @click="openTextModal(row)" :title="row.text">
                                    {{ truncate(row.text, 60) }}
                                </span>
                            </td>
                            <td class="img-cell">
                                <ImagePreview v-if="firstImage(row)" :imageUrl="firstImage(row)!" :maxWidth="40" :maxHeight="40" :finalWidth="220" :finalHeight="220" />
                                <span v-else class="muted">—</span>
                            </td>
                            <td class="price-history-cell">
                                <div v-if="priceChartAsin(row)" class="ph-wrap">
                                    <a :href="`https://keepa.com/#!product/1-${priceChartAsin(row)}`"
                                       target="_blank"
                                       rel="noopener"
                                       :title="`Keepa 90-day price history — ${priceChartAsin(row)}`">
                                        <ImagePreview :imageUrl="priceChartUrl(priceChartAsin(row)!)"
                                                      :maxWidth="160" :maxHeight="40"
                                                      :finalWidth="800" :finalHeight="200" />
                                    </a>
                                </div>
                                <span v-else class="muted">—</span>
                            </td>
                            <td class="grade-cell">
                                <a v-if="effectiveGrade(row) && getDealId(row)" href="#"
                                   @click.prevent="openTunerModal(getDealId(row)!)"
                                   class="grade-badge grade-link" :class="gradeClass(effectiveGrade(row))" :title="gradeTooltip(row)">
                                    {{ effectiveGrade(row) }}<span v-if="isHumanCorrected(row)" class="human-mark" title="Human-corrected">&nbsp;✎</span>
                                </a>
                                <span v-else-if="effectiveGrade(row)" class="grade-badge" :class="gradeClass(effectiveGrade(row))" :title="gradeTooltip(row)">
                                    {{ effectiveGrade(row) }}<span v-if="isHumanCorrected(row)" class="human-mark">&nbsp;✎</span>
                                </span>
                                <span v-else class="muted">—</span>
                            </td>
                            <td class="audience-cell">
                                <a v-if="row.audienceScore != null && getDealId(row)" href="#"
                                   @click.prevent="openAudienceTunerModal(getDealId(row)!)"
                                   class="audience-badge audience-link" :class="audienceClass(row.audienceScore)"
                                   :title="audienceTooltip(row)">
                                    {{ row.audienceScore }}<span v-if="row.audienceScoreSource === 'human'" class="human-mark">&nbsp;✎</span>
                                </a>
                                <span v-else class="muted">—</span>
                            </td>
                            <td class="type-cell">
                                <span v-if="dealTypeCode(row) !== null" class="deal-type-badge" :class="`deal-type-${dealTypeClass(dealTypeCode(row))}`">
                                    {{ dealTypeName(dealTypeCode(row)) }}
                                </span>
                                <span v-else class="muted">—</span>
                            </td>
                            <td class="mono small">{{ row.promoCode || '—' }}</td>
                            <td class="stock-cell">
                                <span v-if="stockLabel(row)" class="stock-badge" :class="stockClass(row)" :title="stockTooltip(row)">
                                    {{ stockLabel(row) }}
                                </span>
                                <span v-else class="muted">—</span>
                            </td>
                            <td class="rank-cell">
                                <span v-if="row.salesRank != null" :title="row.salesRankLabel || ''">#{{ formatRank(row.salesRank) }}</span>
                                <span v-else class="muted">—</span>
                            </td>
                            <td class="seller-cell">
                                <span v-if="row.isSoldByAmazon === true" class="seller-badge seller-amazon" :title="row.buyBoxSeller || ''">Amazon</span>
                                <span v-else-if="row.buyBoxSeller" class="seller-badge seller-3p" :title="row.buyBoxSeller">{{ row.buyBoxSeller }}</span>
                                <span v-else class="muted">—</span>
                            </td>
                            <td class="date-cell">{{ formatDate(row.createdAt) }}</td>
                            <td>
                                <a v-if="row.dealUrl" :href="row.dealUrl" target="_blank" class="deal-link" title="Open deal">↗</a>
                                <span v-else class="muted">—</span>
                            </td>
                            <td>
                                <button
                                    v-if="row.hasApiRawJson"
                                    @click="openJsonModal(row)"
                                    class="json-btn"
                                    title="View ApiRawJson"
                                >{}</button>
                                <span v-else class="muted">—</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="pagination">
                <button @click="prevPage" class="button button-secondary" :disabled="pageNumber === 1 || loading">
                    &laquo; Prev
                </button>
                <span class="page-info">
                    Page {{ pageNumber }} &mdash; {{ rows.length }} rows
                    <span v-if="totalCount != null" class="total-count">
                        / <strong>{{ totalCount.toLocaleString() }}</strong> total
                    </span>
                </span>
                <button @click="nextPage" class="button button-secondary" :disabled="rows.length < pageSize || loading">
                    Next &raquo;
                </button>
                <select v-model.number="pageSize" @change="resetAndLoad" class="form-input page-size-select">
                    <option :value="25">25 / page</option>
                    <option :value="50">50 / page</option>
                    <option :value="100">100 / page</option>
                </select>
            </div>
        </div>

        <!-- All modals rendered via ModalService / ModalContainer (in App.vue) -->
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import BackendHeader from '@/components/BackendHeader.vue'
import ImagePreview from '@/components/Backend/ImagePreview.vue'
import ModalPostText from '@/components/Backend/ModalPostText.vue'
import ModalDealJson from '@/components/Backend/ModalDealJson.vue'
import DealGradeTuner from '@/views/DealGradeTuner.vue'
import AudienceTuner from '@/views/AudienceTuner.vue'
import ModalService from '@/services/modal'
import api from '@/api'

interface PostDealRow {
    postId: number
    dealId: number | null
    source: string
    postState: string
    text: string
    retailer: string | null
    brand: string | null
    category: string | null
    externalId: string | null
    listPrice: number | null
    salePrice: number | null
    finalPrice: number | null
    promoCode: string | null
    dealState: string | null
    createdAt: string
    dealUrl: string | null
    imagesJson: string | null
    hasApiRawJson: boolean
    gaugeTier: string | null
    finalGaugeTier: string | null
    finalGradeSource: string | null
    gaugeBasis: string | null
    score: number | null
    fairP50: number | null
    ratio: number | null
    available: number | null
    availabilityMessage: string | null
    availabilityType: string | null
    availabilitySource: string | null
    salesRank: number | null
    salesRankLabel: string | null
    buyBoxSeller: string | null
    isSoldByAmazon: boolean | null
    finalDealTypeCode: number | null
    audienceScore: number | null
    modelAudienceScore: number | null
    audienceScoreSource: string | null
    audienceConfidence: number | null
}

interface CategoryRow {
    categoryId: number
    name: string
    parentCategoryId: number | null
    path: string
    depth: number | null
    dealCount: number
}

const rows = ref<PostDealRow[]>([])
const totalCount = computed<number | null>(() => {
    const first = rows.value[0] as any
    return first && typeof first.totalCount === 'number' ? first.totalCount : null
})
const loading = ref(false)
const error = ref<string | null>(null)
const searchText = ref('')
const includeArchived = ref(false)
const stateFilter = ref('')
const categoryFilter = ref<number>(0)
const allCategories = ref<CategoryRow[]>([])

// Per-column autofilters — dropdowns for fixed sets, text for free strings.
const fPostId = ref('')
const fSource = ref('')
const fRetailer = ref('amazon')  // start on Amazon-only; user can clear to see everything.
const fBrand = ref('')
const fPromo = ref('')
const fGrade = ref('')
const fType = ref<number | ''>('')
const fSeller = ref('')
// Numeric min/max filters — blank string = no bound. Parsed at request time.
const fFinalMin = ref(''), fFinalMax = ref('')
const fPctOffMin = ref(''),fPctOffMax = ref('')
const fStockMin = ref(''), fStockMax = ref('')
const fRankMin = ref(''),  fRankMax = ref('')
const fAudienceMin = ref(''), fAudienceMax = ref('')

// Multi-column sort: clicking a header cycles asc → desc → removed. Order in the
// list is the ORDER BY precedence (first = primary sort). Backend re-orders NULLs
// to the bottom regardless of direction and tie-breaks on DealId DESC.
type SortEntry = { col: string; dir: 'asc' | 'desc' }
const sortList = ref<SortEntry[]>([{ col: 'CreatedAt', dir: 'desc' }])
const pageNumber = ref(1)
const pageSize = ref(50)
let searchTimer: ReturnType<typeof setTimeout> | null = null

const parseNum = (s: string): number | null => {
    if (!s || !s.trim()) return null
    const n = Number(s)
    return Number.isFinite(n) ? n : null
}

const openTextModal = (row: PostDealRow) => {
    ModalService.open(ModalPostText, { text: row.text ?? '' }, { title: `Post ${row.postId} — Original Text` })
}

const openJsonModal = async (row: PostDealRow) => {
    if (!row.dealId) return
    ModalService.open(ModalDealJson, { dealId: row.dealId }, {
        title: `Post ${row.postId} — ApiRawJson`,
        width: '80vw',
        maxWidth: '1100px',
        height: '80vh'
    })
}

// After a tuner modal closes, refresh ONLY that one row's data and patch it
// into the rows array in place. A full loadData() would lose the user's
// scroll, sort, and filter context, which is annoying when you're labeling
// many deals in a row.
const refreshSingleRow = async (dealId: number) => {
    try {
        const resp = await api.getPostsDealsRow(dealId)
        const fresh = (resp.data as any[])?.[0]
        if (!fresh) return
        const idx = rows.value.findIndex(r => getDealId(r) === dealId)
        if (idx >= 0) rows.value.splice(idx, 1, fresh)
    } catch { /* non-fatal -- row stays as-is until next full reload */ }
}

const openTunerModal = async (dealId: number) => {
    await ModalService.open(DealGradeTuner, { dealId, embedded: true }, {
        title: `Deal Grade Tuner — Deal ${dealId}`,
        width: '95vw',
        maxWidth: '1200px',
        height: '90vh'
    })
    refreshSingleRow(dealId)
}

const openAudienceTunerModal = async (dealId: number) => {
    await ModalService.open(AudienceTuner, { dealId, embedded: true }, {
        title: `Audience Score — Deal ${dealId}`,
        width: '80vw',
        maxWidth: '900px',
        height: 'auto'
    })
    refreshSingleRow(dealId)
}

const topCategories = computed(() =>
    allCategories.value
        .filter(c => (c.depth ?? 0) === 0 || c.parentCategoryId == null)
        .sort((a, b) => (b.dealCount - a.dealCount) || a.name.localeCompare(b.name))
)

const onCategoryChange = () => { pageNumber.value = 1; loadData() }

const loadCategories = async () => {
    try {
        const resp = await api.getCategories()
        allCategories.value = resp.data
    } catch { /* non-fatal */ }
}

const sortQuery = () =>
    sortList.value.length
        ? sortList.value.map(s => `${s.col}:${s.dir}`).join(',')
        : 'CreatedAt:desc'

const loadData = async () => {
    loading.value = true
    error.value = null
    try {
        const resp = await api.getPostsDeals({
            pageSize: pageSize.value,
            pageNumber: pageNumber.value,
            searchText: searchText.value,
            stateFilter: stateFilter.value,
            sort: sortQuery(),
            categoryId: categoryFilter.value || null,
            brandFilter: fBrand.value,
            promoFilter: fPromo.value,
            sourceFilter: fSource.value,
            retailerFilter: fRetailer.value,
            gradeFilter: fGrade.value,
            typeFilter: fType.value === '' ? null : Number(fType.value),
            sellerFilter: fSeller.value,
            postIdFilter: fPostId.value,
            finalMin: parseNum(fFinalMin.value), finalMax: parseNum(fFinalMax.value),
            pctOffMin: parseNum(fPctOffMin.value), pctOffMax: parseNum(fPctOffMax.value),
            stockMin: parseNum(fStockMin.value), stockMax: parseNum(fStockMax.value),
            rankMin: parseNum(fRankMin.value),   rankMax: parseNum(fRankMax.value),
            audienceMin: parseNum(fAudienceMin.value), audienceMax: parseNum(fAudienceMax.value),
            includeArchived: includeArchived.value,
        })
        rows.value = resp.data
    } catch (err: any) {
        error.value = err.response?.data?.message || err.message || 'Failed to load data'
        rows.value = []
    } finally {
        loading.value = false
    }
}

// Debounce text-input filter changes so we don't fire a request per keystroke.
let colFilterTimer: ReturnType<typeof setTimeout> | null = null
const onColumnFilter = () => {
    if (colFilterTimer) clearTimeout(colFilterTimer)
    colFilterTimer = setTimeout(() => { pageNumber.value = 1; loadData() }, 350)
}
const onColumnFilterNow = () => { pageNumber.value = 1; loadData() }

const onSearchInput = () => {
    if (searchTimer) clearTimeout(searchTimer)
    searchTimer = setTimeout(() => { pageNumber.value = 1; loadData() }, 350)
}

// 3-click cycle per column: not-sorted → asc → desc → removed. Click order is preserved
// so users can compose "sort by A, then by B" by clicking A then B.
const setSort = (col: string) => {
    const idx = sortList.value.findIndex(s => s.col === col)
    if (idx === -1) {
        sortList.value = [...sortList.value, { col, dir: 'asc' }]
    } else if (sortList.value[idx].dir === 'asc') {
        const copy = [...sortList.value]
        copy[idx] = { col, dir: 'desc' }
        sortList.value = copy
    } else {
        sortList.value = sortList.value.filter((_, i) => i !== idx)
    }
    pageNumber.value = 1
    loadData()
}

const displayRows = computed(() => rows.value)

const sortIcon = (col: string) => {
    const idx = sortList.value.findIndex(s => s.col === col)
    if (idx === -1) return '⇅'
    const arrow = sortList.value[idx].dir === 'asc' ? '↑' : '↓'
    // Only show a rank number when there are 2+ active sorts — otherwise the bare arrow is cleaner.
    return sortList.value.length > 1 ? `${arrow}${idx + 1}` : arrow
}

const prevPage = () => { if (pageNumber.value > 1) { pageNumber.value--; loadData() } }
const nextPage = () => { pageNumber.value++; loadData() }
const resetAndLoad = () => { pageNumber.value = 1; loadData() }

const truncate = (text: string, maxLen: number) =>
    text && text.length > maxLen ? text.substring(0, maxLen) + '…' : text

const shortSource = (source: string) => {
    if (!source) return '—'
    return source.replace(/^TELEGRAM_/, '').substring(0, 20)
}

const formatDate = (dt: string) => {
    if (!dt) return '—'
    // DB stores UTC (SYSUTCDATETIME). .NET JSON serializer omits the Z, so JS parses
    // as LOCAL unless we explicitly mark it as UTC. Append Z when no timezone present.
    const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(dt)
    return new Date(hasTz ? dt : dt + 'Z').toLocaleString(undefined,
        { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' })
}

const formatPrice = (price: number | null) => price != null ? '$' + price.toFixed(2) : '—'

const firstImage = (row: PostDealRow): string | null => {
    if (!row.imagesJson) return null
    try {
        const imgs = JSON.parse(row.imagesJson)
        if (Array.isArray(imgs) && imgs.length > 0) return imgs[0]
        if (typeof imgs === 'string') return imgs
    } catch { /* ignore */ }
    return null
}

// Inline price-history sparkline — Amazon-only (needs ASIN). Backed by
// Keepa's public PNG endpoint. The backend SQL also computes a KeepaChartUrl
// per row (DataController.cs); we mirror the same URL shape here so the chart
// stays in sync if the backend pattern changes.
const priceChartAsin = (row: PostDealRow): string | null =>
    row.retailer === 'amazon' && row.externalId ? row.externalId : null

const priceChartUrl = (asin: string) =>
    `https://graph.keepa.com/pricehistory.png?asin=${asin}&domain=com&bb=1&new=1&range=90`

const pctOff = (row: PostDealRow): number | null => {
    if (row.listPrice == null || row.finalPrice == null || row.listPrice <= 0) return null
    if (row.finalPrice >= row.listPrice) return null
    return Math.round((1 - row.finalPrice / row.listPrice) * 100)
}

const pctOffClass = (pct: number) => {
    if (pct >= 70) return 'pct-fire'
    if (pct >= 50) return 'pct-great'
    if (pct >= 30) return 'pct-good'
    return 'pct-mild'
}

const effectiveGrade = (row: PostDealRow) => row.finalGaugeTier || row.gaugeTier || null
const isHumanCorrected = (row: PostDealRow) =>
    row.finalGradeSource === 'human'
    && row.finalGaugeTier
    && row.gaugeTier
    && row.finalGaugeTier !== row.gaugeTier

const gradeClass = (tier: string | null) => {
    switch (tier) {
        case 'Great': return 'grade-great'
        case 'Good': return 'grade-good'
        case 'Sale': return 'grade-sale'
        case 'Regular': return 'grade-regular'
        case 'NotADeal': return 'grade-notadeal'
        default: return 'grade-unknown'
    }
}

const gradeTooltip = (row: PostDealRow) => {
    const parts: string[] = []
    if (isHumanCorrected(row)) parts.push(`Human-corrected (model said ${row.gaugeTier})`)
    if (row.fairP50 != null) parts.push(`Fair ~$${row.fairP50.toFixed(2)}`)
    if (row.ratio != null) parts.push(`ratio ${row.ratio.toFixed(2)}`)
    if (row.score != null) parts.push(`score ${row.score}`)
    if (row.gaugeBasis) parts.push(`via ${row.gaugeBasis}`)
    return parts.join(' · ')
}

// DealType mirrors the mapping used on the PostToDealTest page.
const dealTypeCode = (row: PostDealRow): number | null => {
    if (row.finalDealTypeCode !== null && row.finalDealTypeCode !== undefined) return row.finalDealTypeCode
    return null
}

const DEAL_TYPE_NAMES: Record<number, string> = {
    0: 'Unknown',
    1: 'Sale',
    2: 'Coupon',
    3: 'Promo',
    4: 'Sale + Promo',
    5: 'Sale + Coupon',
    6: 'Sale + Coupon + Promo',
    7: 'Coupon + Promo',
    8: 'Good Price'
}
const DEAL_TYPE_CLASSES: Record<number, string> = {
    0: 'unknown',
    1: 'sale-price-deal',
    2: 'coupon-deal',
    3: 'promo-code-deal',
    4: 'sale-price-with-promo-code',
    5: 'sale-price-with-coupon',
    6: 'sale-price-with-coupon-and-promo-code',
    7: 'coupon-and-promo-code-deal',
    8: 'good-price-deal'
}
const audienceTooltip = (row: PostDealRow) => {
    const parts: string[] = []
    if (row.audienceScoreSource === 'human') {
        parts.push('Human-corrected')
        if (row.modelAudienceScore != null) parts.push(`Model said: ${row.modelAudienceScore}`)
    } else if (row.audienceConfidence != null) {
        parts.push(`Confidence: ${row.audienceConfidence}`)
    }
    parts.push('Click to correct')
    return parts.join(' · ')
}

const audienceClass = (score: number) => {
    if (score >= 80) return 'audience-hot'
    if (score >= 60) return 'audience-warm'
    if (score >= 40) return 'audience-mild'
    return 'audience-cold'
}

const getDealId = (row: PostDealRow) => row.dealId || null

const dealTypeName = (code: number | null) => DEAL_TYPE_NAMES[code ?? 0] ?? 'Unknown'
const dealTypeClass = (code: number | null) => DEAL_TYPE_CLASSES[code ?? 0] ?? 'unknown'

const stockLabel = (row: PostDealRow) => {
    if (row.available != null && row.available > 0) return `${row.available} left`
    if (row.availabilityType && /out|unavail/i.test(row.availabilityType)) return 'Out of stock'
    if (row.availabilityMessage) {
        const m = row.availabilityMessage.trim()
        return m.length > 20 ? m.slice(0, 20) + '…' : m
    }
    return ''
}
const stockClass = (row: PostDealRow) => {
    if (row.availabilityType && /out|unavail/i.test(row.availabilityType)) return 'stock-out'
    if (row.available != null && row.available <= 3) return 'stock-low'
    if (row.available != null && row.available <= 10) return 'stock-med'
    return 'stock-ok'
}
const formatRank = (n: number) =>
    n >= 1_000_000 ? (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M'
    : n >= 1_000 ? (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K'
    : String(n)

const stockTooltip = (row: PostDealRow) => {
    const parts: string[] = []
    if (row.available != null) parts.push(`max qty ${row.available}`)
    if (row.availabilityMessage) parts.push(row.availabilityMessage)
    if (row.availabilityType) parts.push(`type: ${row.availabilityType}`)
    if (row.availabilitySource) parts.push(`source: ${row.availabilitySource}`)
    return parts.join(' · ')
}

const stateClass = (state: string) => {
    if (state === 'FAILED') return 'row-failed'
    if (state === 'NEW') return 'row-new'
    return ''
}

const badgeClass = (state: string) => {
    switch (state) {
        case 'NEW': return 'badge-blue'
        case 'PROCESSED': return 'badge-green'
        case 'FAILED': return 'badge-red'
        case 'SKIPPED': return 'badge-gray'
        default: return 'badge-gray'
    }
}

onMounted(() => { loadCategories(); loadData() })
</script>

<style scoped>
.posts-deals-page {
    min-height: 100vh;
    background-color: #f5f5f5;
    padding: 12px;
    text-align: left;
}

.posts-deals-container {
    width: 100%;
    margin: 0;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    padding: 20px;
    box-sizing: border-box;
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 2px solid #e0e0e0;
    flex-wrap: wrap;
    gap: 12px;
}

.page-header h2 { margin: 0; color: #333; }

.controls { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
.archive-toggle { display: flex; align-items: center; gap: 4px; font-size: 12px; color: #666; cursor: pointer; }
.archive-toggle input { cursor: pointer; }
.search-input { min-width: 280px; }

.table-wrap { overflow-x: auto; }

.grid-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
}

.grid-table thead th {
    background: #37474f;
    color: #fff;
    padding: 6px 7px;
    text-align: left;
    white-space: nowrap;
    border-right: 1px solid #546e7a;
    user-select: none;
}

.grid-table thead th:last-child { border-right: none; }
.sortable { cursor: pointer; }
.sortable:hover { background: #455a64; }
.sort-icon { opacity: 0.7; margin-left: 3px; font-size: 10px; }

.grid-table tbody tr { border-bottom: 1px solid #eee; }
.grid-table tbody tr:hover { background: #f1f8ff; }
.grid-table tbody tr.row-failed { background: #fff8f8; }
.grid-table tbody tr.row-new { background: #f8f9ff; }

.grid-table td { padding: 4px 7px; vertical-align: middle; color: #333; }

.col-source { width: 90px; }
.img-cell { width: 48px; padding: 4px 6px; vertical-align: middle; }
.price-history-cell { width: 192px; padding: 2px 4px; vertical-align: middle; text-align: center; }
.price-history-cell .ph-wrap { display: inline-flex; align-items: center; gap: 4px; }
.price-history-cell .keepa-h {
    display: inline-block;
    width: 18px; height: 18px; line-height: 18px;
    border-radius: 3px;
    background: #ff9900;
    color: #fff;
    font-weight: 700;
    font-size: 11px;
    text-align: center;
    text-decoration: none;
    flex: 0 0 auto;
}
.price-history-cell .keepa-h:hover { background: #e07a00; }

.source-cell {
    max-width: 80px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 10px;
    color: #777;
}

.mono { font-family: 'Courier New', monospace; font-size: 10px; }
.small { font-size: 10px; }
.muted { color: #bbb; }

.text-cell { min-width: 220px; max-width: 300px; }
.text-link {
    cursor: pointer;
    color: #1565c0;
    text-decoration: underline dotted;
    overflow-wrap: anywhere;
    word-break: normal;
    font-size: 11px;
    display: inline-block;
    max-width: 100%;
}
.text-link:hover { color: #0d47a1; }

.price-cell { white-space: nowrap; font-size: 11px; font-weight: 500; text-align: right; }
.price-final { color: #2e7d32; font-weight: 700; }

.price-cell-combined {
    white-space: nowrap;
    text-align: right;
    padding: 3px 8px;
    line-height: 1.2;
}
.price-cell-combined .p-final {
    color: #2e7d32;
    font-weight: 700;
    font-size: 14px;
}
.price-cell-combined .p-strike {
    color: #999;
    text-decoration: line-through;
    font-size: 11px;
}
.price-cell-combined .p-faint { color: #bbb; font-size: 10px; }

.date-cell { white-space: nowrap; font-size: 10px; color: #888; }

.pct-off-cell { white-space: nowrap; text-align: right; }
.pct-off {
    display: inline-block;
    padding: 1px 6px;
    border-radius: 10px;
    font-size: 10px;
    font-weight: 700;
}
.pct-mild  { background: #fce4e4; color: #8b1a1a; }
.pct-good  { background: #f8c0c0; color: #7b0000; }
.pct-great { background: #ef9a9a; color: #5a0000; }
.pct-fire  { background: #c62828; color: #fff; }

.grade-cell { white-space: nowrap; text-align: center; width: 64px; }
.grade-badge {
    display: inline-block;
    padding: 1px 6px;
    border-radius: 10px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.3px;
    cursor: help;
}
.grade-notadeal{ background: #d32f2f; color: #fff; }
.grade-great   { background: #2e7d32; color: #fff; }
.grade-good    { background: #66bb6a; color: #fff; }
.grade-sale    { background: #fff3c4; color: #8a6d00; }
.grade-regular { background: #eceff1; color: #555; }
.grade-unknown { background: #f5f5f5; color: #aaa; }
.grade-link { text-decoration: none; cursor: pointer; transition: opacity 0.15s; }
.grade-link:hover { opacity: 0.8; }
.human-mark { font-size: 9px; opacity: 0.9; }
.audience-cell { white-space: nowrap; text-align: center; width: 48px; }
.audience-badge { display: inline-block; padding: 1px 6px; border-radius: 10px; font-weight: 700; font-size: 10px; cursor: help; }
.audience-link { text-decoration: none; cursor: pointer; transition: opacity 0.15s; }
.audience-link:hover { opacity: 0.8; }
.audience-hot  { background: #2e7d32; color: #fff; }
.audience-warm { background: #1565c0; color: #fff; }
.audience-mild { background: #eceff1; color: #555; }
.audience-cold { background: #f5f5f5; color: #bbb; }

.type-cell { white-space: nowrap; font-size: 10px; width: 110px; }
.deal-type-badge {
    display: inline-block;
    padding: 2px 6px;
    border-radius: 6px;
    font-weight: 600;
    font-size: 10px;
    line-height: 1.3;
    white-space: nowrap;
}
.deal-type-badge.deal-type-unknown { background: #f1f5f9; color: #475569; }
.deal-type-badge.deal-type-sale-price-deal { background: #e0f2fe; color: #075985; }
.deal-type-badge.deal-type-coupon-deal { background: #fff7ed; color: #9a3412; }
.deal-type-badge.deal-type-promo-code-deal { background: #f5f3ff; color: #5b21b6; }
.deal-type-badge.deal-type-sale-price-with-promo-code { background: #dcfce7; color: #166534; }
.deal-type-badge.deal-type-sale-price-with-coupon { background: #fef3c7; color: #92400e; }
.deal-type-badge.deal-type-sale-price-with-coupon-and-promo-code { background: #fce7f3; color: #9d174d; }
.deal-type-badge.deal-type-coupon-and-promo-code-deal { background: #ede9fe; color: #6d28d9; }
.deal-type-badge.deal-type-good-price-deal { background: #ecfccb; color: #3f6212; }

.brand-cell { font-size: 10px; color: #444; max-width: 100px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cat-cell { font-size: 10px; color: #666; max-width: 100px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.stock-cell { white-space: nowrap; font-size: 10px; }
.rank-cell { white-space: nowrap; font-size: 10px; text-align: right; color: #555; }

.filter-row th { background: #455a64; padding: 3px 4px; }
.col-filter { width: 100%; max-width: 130px; padding: 2px 5px; border: 1px solid #607d8b; border-radius: 3px; background: #eceff1; font-size: 10px; color: #222; }
.col-filter:focus { outline: none; border-color: #ffeb3b; background: #fff; }
.range-filter { display: flex; gap: 2px; max-width: 130px; }
.range-input { width: 50%; min-width: 0; padding: 2px 3px; text-align: right; }
.range-input::placeholder { color: #90a4ae; font-style: italic; }
.stock-badge { display: inline-block; padding: 1px 6px; border-radius: 10px; font-weight: 600; font-size: 10px; cursor: help; }
.stock-ok   { background: #e8f5e9; color: #2e7d32; }
.stock-med  { background: #fff3c4; color: #8a6d00; }
.stock-low  { background: #ffe0b2; color: #b45309; }
.stock-out  { background: #ffebee; color: #c62828; }

.seller-cell { white-space: nowrap; font-size: 10px; max-width: 140px; overflow: hidden; text-overflow: ellipsis; }
.seller-badge { display: inline-block; padding: 1px 6px; border-radius: 10px; font-weight: 600; font-size: 10px; max-width: 130px; overflow: hidden; text-overflow: ellipsis; vertical-align: middle; }
.seller-amazon { background: #fff3e0; color: #e65100; }
.seller-3p     { background: #eceff1; color: #455a64; }

.deal-link {
    font-size: 16px;
    color: #1565c0;
    text-decoration: none;
    display: inline-block;
    line-height: 1;
}
.deal-link:hover { color: #0d47a1; }

.loading-cell, .empty-cell { text-align: center; padding: 40px; color: #999; }

/* Badges */
.badge {
    display: inline-block;
    padding: 2px 7px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
}
.badge-blue  { background: #e3f2fd; color: #1565c0; }
.badge-green { background: #e8f5e9; color: #2e7d32; }
.badge-red   { background: #ffebee; color: #c62828; }
.badge-gray  { background: #f5f5f5; color: #555; }

/* Pagination */
.pagination { display: flex; align-items: center; gap: 12px; margin-top: 16px; flex-wrap: wrap; }
.page-info { color: #666; font-size: 14px; }
.total-count { color: #1565c0; margin-left: 6px; }
.total-count strong { color: #0d47a1; }
.header-total { font-size: 14px; font-weight: 400; color: #666; margin-left: 8px; }
.header-total strong { color: #0d47a1; }
.page-size-select { width: auto; }

/* Buttons */
.button {
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
    transition: background-color 0.2s;
}
.button:disabled { opacity: 0.5; cursor: not-allowed; }
.button-primary { background: #2196f3; color: #fff; }
.button-primary:hover:not(:disabled) { background: #1976d2; }
.button-secondary { background: #eceff1; color: #333; }
.button-secondary:hover:not(:disabled) { background: #cfd8dc; }
.button-sm { padding: 3px 9px; font-size: 11px; background: #7e57c2; color: #fff; border-radius: 4px; border: none; cursor: pointer; }
.button-sm:hover { background: #5e35b1; }
.json-btn {
    padding: 0 4px;
    font-size: 10px;
    font-family: 'Courier New', monospace;
    font-weight: 700;
    line-height: 16px;
    background: #7e57c2;
    color: #fff;
    border-radius: 3px;
    border: none;
    cursor: pointer;
}
.json-btn:hover { background: #5e35b1; }

/* Form inputs */
.form-input { padding: 8px 12px; border: 1px solid #ddd; border-radius: 4px; font-size: 13px; }
.form-input:focus { outline: none; border-color: #2196f3; }

/* Error */
.error-message { color: #c62828; background: #ffebee; border-radius: 4px; padding: 12px; margin-bottom: 12px; }

</style>
